import {
  demoAdminBackend,
  demoAdminLogin as demoAdmin,
  demoColleagueLogin as demoColleague,
  demoMarketingLogin as demoMarketing,
  demoTwoFactorHint as demoHint,
} from '../demo/adminBackend'
import { ApiError, call, hasBackend, newRequestId, nullOn } from '../core/api/http'
import { notifyAdminAccessLost } from './accessLost'
import { endpoints } from '../core/api/endpoints'
import { normalizeLiabilities, normalizeSalesReport, normalizeSalonInsights, normalizeWebshopInsights } from '../core/rules/reportShape'
import type { Appointment, Course, Customer, DiaryEntry, SkinPlan, Subscription, Tracking } from '../core/types/account'
import type { AdminBackend, DemoHint, DemoLogin, StockOverview } from '../core/types/backend'
import type {
  NewStockItemRequest,
  PurchaseReceipt,
  PurchaseReceiptRequest,
  StockCount,
  StockCountRequest,
  StockCountResult,
  StockItem,
  StockItemPatch,
  StockMovement,
  StockMovementRequest,
} from '../core/types/stock'
import type { SubscriptionAction } from '../core/types/requests'
import type {
  BusinessProfileReport,
  CustomerInsights,
  InsightsOverview,
  IntegrationId,
  Integrations,
  IntegrationStatus,
  MailInsights,
  SalonInsights,
  SearchReport,
  SocialReport,
  SourceInsights,
  VisitorsReport,
  WebshopInsights,
} from '../core/types/insights'
import type {
  AdminContent,
  StockUpdateResponse,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminUser,
  CompleteResponse,
  RecoveryCodesResponse,
  TwoFactorChallengeRequest,
  TwoFactorConfirmResponse,
  TwoFactorSetup,
  CustomerFile,
  CustomerCreateRequest,
  CustomerContactRequest,
  CustomerSummary,
  DiaryMarkRequest,
  StaffCustomerCard,
  StaffLogin,
  FulfilmentStatus,
  Inbox,
  InboxBooking,
  InboxOrder,
  NewAppointmentRequest,
  NoteRequest,
  BookingBlockRequest,
  BookingBlockResponse,
  ProposeRequest,
  AdminOrder,
  AdminOrderPage,
  AdminOrderQuery,
  CancelOrderRequest,
  RefundRequest,
  SalesChannel,
  SalesGroupBy,
  SalesReport,
} from '../core/types/adminRequests'
import type { RequestOptions } from '../core/types/backend'
import type {
  AgendaAppointment,
  AgendaBlock,
  AgendaBlockRequest,
  AgendaCompleteRequest,
  AgendaFeed,
  AgendaMoveRequest,
  AgendaAbsenceRequest,
  AgendaAbsenceResult,
  AgendaNewRequest,
  AgendaSeriesRequest,
  AgendaSeriesResult,
  AgendaRange,
} from '../core/types/agenda'
import type {
  CreditCorrectionRequest,
  CustomerCredit,
  LiabilitiesReport,
  NewVoucherRequest,
  Voucher,
  VoucherBlockRequest,
  VoucherCorrectionRequest,
  VoucherPage,
  VoucherQuery,
} from '../core/types/vouchers'
import type { RedirectsRequest, RedirectsResponse } from '../core/types/seo'
import type { CashDayClose, CloseDayRequest, KassaDay, KuurStop, KuurStopRequest, Sale, SaleRequest, SaleReturn, SaleReturnRequest, VoidSaleRequest } from '../core/types/kassa'
import type { HandoffNote, HandoffNoteRequest } from '../core/types/handoff'
import type { AuditPage, AuditQuery, ReauthRequest, StaffAccessRequest } from '../core/types/team'
import type { OkResponse } from '../core/types/requests'
import type {
  MailSettings,
  MailTestRequest,
  NewsletterDeliveryResponse,
  NewsletterScheduleRequest,
  NewsletterSendRequest,
  SegmentList,
  SegmentsRequest,
  SubscriberPage,
  SubscriberQuery,
} from '../core/types/mail'

/**
 * Beheer ↔ backend: inloggen, het concept, klanten en de inbox. Paden:
 * core/api/endpoints.ts. Zonder backend speelt demo/adminBackend.ts de rol na.
 * Klantdata wordt per actie bewaard, niet via "Opslaan" in de concept-balk.
 */

const E = endpoints.admin

// Antwoord- en bodytypen komen uit core/api/endpoints.ts; hier geen generics.
const http: AdminBackend = {
  // Sessie via een httpOnly-cookie op het beheer-domein. Nooit een token in de browseropslag.
  fetchAdminSession: () => nullOn(401, call(E.me, [])),
  adminLogin: (req) => call(E.login, [], req),
  adminLogout: () => call(E.logout, []),
  twoFactorChallenge: (req) => call(E.twoFactorChallenge, [], req),
  twoFactorSetup: () => call(E.twoFactorSetup, []),
  twoFactorConfirm: (req) => call(E.twoFactorConfirm, [], req),
  regenerateRecoveryCodes: () => call(E.recoveryCodes, []),
  fetchAdminContent: () => nullOn(404, call(E.content, [])),
  saveAdminContent: (content) => call(E.saveContent, [], content),
  updateStock: (productId, req) => call(E.productStock, [productId], req),
  fetchCustomers: (opts) => call(E.customers, [], undefined, opts),
  exportCustomers: () => call(E.customersExport, []),
  fetchCustomer: (id, opts) => nullOn(404, call(E.customer, [id], undefined, opts)),
  createCustomer: (req) => call(E.customerCreate, [], req),
  mergeCustomers: (id, req) => call(E.customerMerge, [id], req),
  saveCustomerContact: (id, req) => call(E.customerContact, [id], req),
  savePlan: (id, plan) => call(E.plan, [id], plan),
  saveCourse: (id, course) => call(E.course, [id], course),
  stopKuur: (id, req) => call(E.kuurStop, [id], req),
  saveNote: (id, req) => call(E.note, [id], req),
  saveBookingBlock: (id, req) => call(E.bookingBlock, [id], req),
  addAppointment: (id, req) => call(E.addAppointment, [id], req),
  confirmAppointment: (id, aid) => call(E.confirmAppointment, [id, aid]),
  proposeAppointment: (id, aid, req) => call(E.proposeAppointment, [id, aid], req),
  completeAppointment: (id, aid) => call(E.completeAppointment, [id, aid]),
  removeAppointment: (id, aid) => call(E.removeAppointment, [id, aid]),
  customerSubscriptionAction: (id, action) => call(E.subscriptionAction, [id], action),
  markDiaryEntry: (id, entryId, req) => call(E.diaryMark, [id, entryId], req),
  fetchInbox: (opts) => call(E.inbox, [], undefined, opts),
  setOrderStatus: (orderId, status, tracking) => call(E.orderStatus, [orderId], tracking ? { status, tracking } : { status }),
  confirmGuestBooking: (id) => call(E.confirmGuestBooking, [id]),
  fetchAgenda: (from, to, opts) => call(E.agenda, [from, to], undefined, opts),
  addAgendaAppointment: (req) => call(E.agendaAdd, [], req),
  addAgendaSeries: (req) => call(E.agendaSeries, [], req),
  moveAgendaAppointment: (id, req) => call(E.agendaMove, [id], req),
  confirmAgendaAppointment: (id) => call(E.agendaConfirm, [id]),
  arriveAgendaAppointment: (id) => call(E.agendaArrive, [id]),
  completeAgendaAppointment: (id, req) => call(E.agendaComplete, [id], req),
  noShowAgendaAppointment: (id) => call(E.agendaNoShow, [id]),
  cancelAgendaAppointment: (id) => call(E.agendaCancel, [id]),
  addAgendaBlock: (req) => call(E.agendaAddBlock, [], req),
  removeAgendaBlock: (id) => call(E.agendaRemoveBlock, [id]),
  reportAbsence: (req) => call(E.agendaAbsence, [], req),
  fetchAgendaFeed: () => call(E.agendaFeed, []),
  fetchStaffLogins: (opts) => call(E.staffLogins, [], undefined, opts),
  inviteStaff: (staffId, req) => call(E.staffInvite, [staffId], req),
  revokeStaffLogin: (staffId) => call(E.staffRevoke, [staffId]),
  updateStaffAccess: (staffId, req) => call(E.staffAccess, [staffId], req),
  stopStaff: (staffId) => call(E.staffStop, [staffId]),
  revokeStaffSessions: (staffId) => call(E.staffSessionsRevoke, [staffId]),
  reauth: (req) => call(E.reauth, [], req),
  fetchAuditLog: (query, opts) => call(E.audit, [query], undefined, opts),
  fetchStaffCustomer: (id, opts) => call(E.staffCustomer, [id], undefined, opts),
  fetchHandoffNotes: (opts) => call(E.handoffNotes, [], undefined, opts),
  addHandoffNote: (customerId, req) => call(E.handoffNoteAdd, [customerId], req),
  markHandoffNoteRead: (id) => call(E.handoffNoteRead, [id]),
  fetchOrders: (query, opts) => call(E.orders, [query], undefined, opts),
  fetchOrder: (id, opts) => nullOn(404, call(E.order, [id], undefined, opts)),
  refundOrder: (id, req) => call(E.orderRefund, [id], req),
  cancelOrder: (id, req) => call(E.orderCancel, [id], req),
  fetchSalesReport: (from, to, groupBy, channel, opts) => call(E.salesReport, [from, to, groupBy, channel], undefined, opts),
  exportSales: (from, to) => call(E.salesExport, [from, to]),
  fetchInsightsOverview: (from, to, opts) => call(E.insightsOverview, [from, to], undefined, opts),
  fetchInsightsCustomers: (from, to, opts) => call(E.insightsCustomers, [from, to], undefined, opts),
  fetchInsightsSalon: (from, to, opts) => call(E.insightsSalon, [from, to], undefined, opts),
  fetchInsightsWebshop: (from, to, opts) => call(E.insightsWebshop, [from, to], undefined, opts),
  fetchInsightsSources: (from, to, opts) => call(E.insightsSources, [from, to], undefined, opts),
  // 404 `not_connected`: de bron is niet gekoppeld; het scherm toont dan de uitleg.
  fetchInsightsVisitors: (from, to, opts) => nullOn(404, call(E.insightsVisitors, [from, to], undefined, opts)),
  fetchInsightsSearch: (from, to, opts) => nullOn(404, call(E.insightsSearch, [from, to], undefined, opts)),
  fetchInsightsBusiness: (from, to, opts) => nullOn(404, call(E.insightsBusiness, [from, to], undefined, opts)),
  fetchInsightsSocial: (from, to, opts) => nullOn(404, call(E.insightsSocial, [from, to], undefined, opts)),
  fetchInsightsMail: (from, to, opts) => nullOn(404, call(E.insightsMail, [from, to], undefined, opts)),
  fetchIntegrations: (opts) => call(E.integrations, [], undefined, opts),
  refreshIntegration: (id) => call(E.integrationRefresh, [id]),
  fetchStock: (opts) => call(E.stock, [], undefined, opts),
  fetchStockMovements: (itemId, opts) => call(E.stockMovements, [itemId], undefined, opts),
  addStockMovement: (req) => call(E.stockMovementAdd, [], req),
  addStockItem: (req) => call(E.stockItemAdd, [], req),
  patchStockItem: (id, patch) => call(E.stockItemPatch, [id], patch),
  exportStockMovements: (from, to) => call(E.stockMovementsExport, [from, to]),
  fetchStockReceipts: (opts) => call(E.stockReceipts, [], undefined, opts),
  addStockReceipt: (req) => call(E.stockReceiptAdd, [], req),
  fetchStockCounts: (opts) => call(E.stockCounts, [], undefined, opts),
  addStockCount: (req) => call(E.stockCountAdd, [], req),
  fetchKassaDay: (day, opts) => call(E.kassaDay, [day], undefined, opts),
  createSale: (req) => call(E.saleCreate, [], req),
  voidSale: (id, req) => call(E.saleVoid, [id], req),
  returnSale: (id, req) => call(E.saleReturn, [id], req),
  closeKassaDay: (day, req) => call(E.kassaClose, [day], req),
  fetchCashDayCloses: (from, to, opts) => call(E.kassaCloses, [from, to], undefined, opts),
  fetchVouchers: (query, opts) => call(E.vouchers, [query], undefined, opts),
  fetchVoucher: (id, opts) => nullOn(404, call(E.voucher, [id], undefined, opts)),
  createVoucher: (req) => call(E.voucherCreate, [], req),
  correctVoucher: (id, req) => call(E.voucherCorrect, [id], req),
  blockVoucher: (id, req) => call(E.voucherBlock, [id], req),
  fetchLiabilities: (on, opts) => call(E.liabilitiesReport, [on], undefined, opts),
  fetchCustomerCredit: (id, opts) => call(E.customerCredit, [id], undefined, opts),
  correctCustomerCredit: (id, req) => call(E.customerCreditCorrect, [id], req),
  fetchRedirects: (opts) => call(E.redirects, [], undefined, opts),
  saveRedirects: (req) => call(E.redirectsSave, [], req),
  fetchMailSettings: (opts) => call(E.mails, [], undefined, opts),
  saveMailSettings: (settings) => call(E.mailsSave, [], settings),
  sendMailTest: (key, req) => call(E.mailTest, [key], req),
  sendNewsletter: (id, req) => call(E.newsletterSend, [id], req),
  scheduleNewsletter: (id, req) => call(E.newsletterSchedule, [id], req),
  sendNewsletterTest: (id, req) => call(E.newsletterTest, [id], req),
  fetchSubscribers: (query, opts) => call(E.subscribers, [query], undefined, opts),
  fetchSegments: (opts) => call(E.segments, [], undefined, opts),
  saveSegments: (req) => call(E.segmentsSave, [], req),
}

/**
 * Elke aanvraag (behalve inloggen en de sessiecheck) die 401 geeft, betekent: sessie verlopen, of de
 * collega is gestopt of van rol veranderd. Dan terug naar inloggen (admin/accessLost.ts).
 */
const SESSION_CALLS = new Set<keyof AdminBackend>(['fetchAdminSession', 'adminLogin', 'twoFactorChallenge'])

function guarded(backend: AdminBackend): AdminBackend {
  const wrapped = {} as Record<string, unknown>
  for (const [key, fn] of Object.entries(backend) as [keyof AdminBackend, unknown][]) {
    if (typeof fn !== 'function') continue
    wrapped[key] = (...args: unknown[]) =>
      (fn as (...a: unknown[]) => Promise<unknown>).apply(backend, args).catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401 && !SESSION_CALLS.has(key)) notifyAdminAccessLost()
        throw err
      })
  }
  return wrapped as unknown as AdminBackend
}

const impl: AdminBackend = guarded(hasBackend ? http : demoAdminBackend)

/** Alleen in de demo: het testaccount om snel in te vullen. Met een backend `null`. */
export const demoAdminLogin: DemoLogin | null = hasBackend ? null : demoAdmin
/** Alleen in de demo: het testaccount van de collega. Met een backend `null`. */
export const demoColleagueLogin: DemoLogin | null = hasBackend ? null : demoColleague
/** Alleen in de demo: het testaccount voor marketing. Met een backend `null`. */
export const demoMarketingLogin: DemoLogin | null = hasBackend ? null : demoMarketing
/** Alleen in de demo: de code die de authenticator-app nu zou tonen. Met een backend `null`. */
export const demoTwoFactorHint: (() => Promise<DemoHint | null>) | null = hasBackend ? null : demoHint

// ——— Inloggen ———

/** Wie er ingelogd is, of `null`. */
export const fetchAdminSession = async (): Promise<AdminUser | null> => impl.fetchAdminSession()
export const adminLogin = async (req: AdminLoginRequest): Promise<AdminLoginResponse> => impl.adminLogin(req)
export const adminLogout = async (): Promise<void> => impl.adminLogout()

// ——— Tweestapsverificatie ———

export const twoFactorChallenge = async (req: TwoFactorChallengeRequest): Promise<AdminUser> => impl.twoFactorChallenge(req)
export const twoFactorSetup = async (): Promise<TwoFactorSetup> => impl.twoFactorSetup()
export const twoFactorConfirm = async (code: string): Promise<TwoFactorConfirmResponse> => impl.twoFactorConfirm({ code })
export const regenerateRecoveryCodes = async (): Promise<RecoveryCodesResponse> => impl.regenerateRecoveryCodes()

// ——— Inhoud: catalogus, instellingen, paginateksten en marketing ———

/** Het laatst opgeslagen concept, of `null` — dan blijft de lokale data gelden. */
export const fetchAdminContent = async (): Promise<AdminContent | null> => impl.fetchAdminContent()
/** "Opslaan" in de concept-balk: alles in één keer, behalve voorraad. 409 `version_conflict` bij een verouderd concept. */
export const saveAdminContent = async (content: AdminContent): Promise<AdminContent> => impl.saveAdminContent(content)
/** Voorraad meteen opslaan. `applied: false` = er is intussen iets verkocht; `stock` is dan de actuele stand. */
export const updateStock = async (productId: string, stock: number | null, expected: number | null): Promise<StockUpdateResponse> =>
  impl.updateStock(productId, { stock, expected })

// ——— Klanten ———

export const fetchCustomers = async (opts?: RequestOptions): Promise<CustomerSummary[]> => impl.fetchCustomers(opts)
export const exportCustomers = async (): Promise<CustomerSummary[]> => impl.exportCustomers()
/** `null` als de klant niet bestaat. */
export const fetchCustomer = async (id: string, opts?: RequestOptions): Promise<CustomerFile | null> =>
  impl.fetchCustomer(id, opts)
/** Nieuwe klant met e-mail óf telefoon. Nooit optimistisch: de server bepaalt de id. */
export const createCustomer = async (req: CustomerCreateRequest): Promise<{ id: string }> => impl.createCustomer(req)
/** Het andere dossier gaat op in dit dossier. Nooit optimistisch. */
export const mergeCustomers = async (id: string, sourceId: string): Promise<CustomerFile> => impl.mergeCustomers(id, { sourceId })
/** Naam, telefoon en (zonder login) e-mail. Nooit optimistisch: de server controleert dubbelen. */
export const saveCustomerContact = async (id: string, req: CustomerContactRequest): Promise<Customer> => impl.saveCustomerContact(id, req)
export const savePlan = async (id: string, plan: SkinPlan): Promise<SkinPlan> => impl.savePlan(id, plan)
export const saveCourse = async (id: string, course: Course | null): Promise<Course | null> => impl.saveCourse(id, course)
/** Kuur stoppen en terugbetalen. Nooit optimistisch: het is geld. */
export const stopKuur = async (id: string, req: KuurStopRequest): Promise<KuurStop> => impl.stopKuur(id, req)
export const saveNote = async (id: string, internalNote: string): Promise<NoteRequest> => impl.saveNote(id, { internalNote })
export const saveBookingBlock = async (id: string, req: BookingBlockRequest): Promise<BookingBlockResponse> => impl.saveBookingBlock(id, req)
export const addAppointment = async (id: string, req: NewAppointmentRequest): Promise<Appointment> => impl.addAppointment(id, req)
export const confirmAppointment = async (id: string, aid: string): Promise<Appointment> => impl.confirmAppointment(id, aid)
export const proposeAppointment = async (id: string, aid: string, req: ProposeRequest): Promise<Appointment> =>
  impl.proposeAppointment(id, aid, req)
/** Afspraak afronden: wordt een bezoek, en telt mee in de kuur als het een kuursessie was. */
export const completeAppointment = async (id: string, aid: string): Promise<CompleteResponse> => impl.completeAppointment(id, aid)
export const removeAppointment = async (id: string, aid: string): Promise<void> => impl.removeAppointment(id, aid)
/** Eén `requestId` per actie, zoals in Mijn omgeving. */
export const customerSubscriptionAction = async (id: string, action: SubscriptionAction): Promise<Subscription | null> =>
  impl.customerSubscriptionAction(id, { ...action, requestId: newRequestId() })
export const markDiaryEntry = async (id: string, entryId: string, req: DiaryMarkRequest): Promise<DiaryEntry> =>
  impl.markDiaryEntry(id, entryId, req)

// ——— Inbox ———

export const fetchInbox = async (opts?: RequestOptions): Promise<Inbox> => impl.fetchInbox(opts)
/** Verzonden → de bestelling staat in Mijn omgeving op "Onderweg", met track & trace als die is ingevuld. */
export const setOrderStatus = async (orderId: string, status: FulfilmentStatus, tracking?: Tracking): Promise<InboxOrder> =>
  impl.setOrderStatus(orderId, status, tracking)

// ——— Agenda ———

/** Alles tussen twee datums ("YYYY-MM-DD", inclusief). */
export const fetchAgenda = async (from: string, to: string, opts?: RequestOptions): Promise<AgendaRange> => impl.fetchAgenda(from, to, opts)
/** Door Sarah ingepland = direct bevestigd. 409 `slot_taken` bij overlap, tenzij `allowOverlap`. */
export const addAgendaAppointment = async (req: AgendaNewRequest): Promise<AgendaAppointment> => impl.addAgendaAppointment(req)
export const addAgendaSeries = async (req: AgendaSeriesRequest): Promise<AgendaSeriesResult> => impl.addAgendaSeries(req)
export const moveAgendaAppointment = async (id: string, req: AgendaMoveRequest): Promise<AgendaAppointment> =>
  impl.moveAgendaAppointment(id, req)
export const confirmAgendaAppointment = async (id: string): Promise<AgendaAppointment> => impl.confirmAgendaAppointment(id)
export const arriveAgendaAppointment = async (id: string): Promise<AgendaAppointment> => impl.arriveAgendaAppointment(id)
export const completeAgendaAppointment = async (id: string, req: AgendaCompleteRequest): Promise<AgendaAppointment> =>
  impl.completeAgendaAppointment(id, req)
export const noShowAgendaAppointment = async (id: string): Promise<AgendaAppointment> => impl.noShowAgendaAppointment(id)
export const cancelAgendaAppointment = async (id: string): Promise<AgendaAppointment> => impl.cancelAgendaAppointment(id)
export const addAgendaBlock = async (req: AgendaBlockRequest): Promise<AgendaBlock> => impl.addAgendaBlock(req)
export const removeAgendaBlock = async (id: string): Promise<void> => impl.removeAgendaBlock(id)
export const reportAbsence = async (req: AgendaAbsenceRequest): Promise<AgendaAbsenceResult> => impl.reportAbsence(req)
export const fetchAgendaFeed = async (): Promise<AgendaFeed> => impl.fetchAgendaFeed()

// ——— Medewerkers ———

export const fetchStaffLogins = async (opts?: RequestOptions): Promise<StaffLogin[]> => impl.fetchStaffLogins(opts)
/** Uitnodiging per mail. 409 `email_taken` als het adres al bij een ander account hoort. */
export const inviteStaff = async (staffId: string, email: string): Promise<StaffLogin> => impl.inviteStaff(staffId, { email })
export const revokeStaffLogin = async (staffId: string): Promise<void> => impl.revokeStaffLogin(staffId)
/** Rol en einddatum. 409 `last_owner`; 423 `reauth_required` zonder recente bevestiging. */
export const updateStaffAccess = async (staffId: string, req: StaffAccessRequest): Promise<StaffLogin> => impl.updateStaffAccess(staffId, req)
/** Stoppen in één keer: persoon inactief, login en sessies weg. */
export const stopStaff = async (staffId: string): Promise<void> => impl.stopStaff(staffId)
export const revokeStaffSessions = async (staffId: string): Promise<StaffLogin> => impl.revokeStaffSessions(staffId)
/** Opnieuw bevestigen wie je bent. 422 = wachtwoord of code klopt niet. */
export const reauth = async (req: ReauthRequest): Promise<void> => impl.reauth(req)
export const fetchAuditLog = async (query: AuditQuery, opts?: RequestOptions): Promise<AuditPage> => impl.fetchAuditLog(query, opts)
/** De beperkte klantkaart voor een collega. 403 als de klant geen afspraak bij haar heeft. */
export const fetchStaffCustomer = async (id: string, opts?: RequestOptions): Promise<StaffCustomerCard> => impl.fetchStaffCustomer(id, opts)
export const fetchHandoffNotes = async (opts?: RequestOptions): Promise<HandoffNote[]> => impl.fetchHandoffNotes(opts)
export const addHandoffNote = async (customerId: string, req: HandoffNoteRequest): Promise<HandoffNote> => impl.addHandoffNote(customerId, req)
export const markHandoffNoteRead = async (id: string): Promise<HandoffNote> => impl.markHandoffNoteRead(id)

// ——— Bestellingen en omzet ———

export const fetchOrders = async (query: AdminOrderQuery, opts?: RequestOptions): Promise<AdminOrderPage> => impl.fetchOrders(query, opts)
/** `null` als de bestelling niet bestaat. */
export const fetchOrder = async (id: string, opts?: RequestOptions): Promise<AdminOrder | null> => impl.fetchOrder(id, opts)
/** Terugbetalen via Mollie. Eén `requestId` per poging: een herhaling betaalt nooit twee keer. */
export const refundOrder = async (id: string, req: RefundRequest): Promise<AdminOrder> => impl.refundOrder(id, req)
export const cancelOrder = async (id: string, req: CancelOrderRequest): Promise<AdminOrder> => impl.cancelOrder(id, req)
export const fetchSalesReport = async (
  from: string,
  to: string,
  groupBy: SalesGroupBy,
  channel: SalesChannel | undefined,
  opts?: RequestOptions,
): Promise<SalesReport> => normalizeSalesReport(await impl.fetchSalesReport(from, to, groupBy, channel, opts))
export const exportSales = async (from: string, to: string): Promise<string> => impl.exportSales(from, to)

// ——— Inzicht (alleen lezen). Externe bronnen: `null` = niet gekoppeld ———

type InsightFetch<T> = (from: string, to: string, opts?: RequestOptions) => Promise<T>

export const fetchInsightsOverview: InsightFetch<InsightsOverview> = async (from, to, opts) => impl.fetchInsightsOverview(from, to, opts)
export const fetchInsightsCustomers: InsightFetch<CustomerInsights> = async (from, to, opts) => impl.fetchInsightsCustomers(from, to, opts)
// Oudere server: ontbrekende, later toegevoegde velden worden leeg (core/rules/reportShape.ts).
export const fetchInsightsSalon: InsightFetch<SalonInsights> = async (from, to, opts) => normalizeSalonInsights(await impl.fetchInsightsSalon(from, to, opts))
export const fetchInsightsWebshop: InsightFetch<WebshopInsights> = async (from, to, opts) => normalizeWebshopInsights(await impl.fetchInsightsWebshop(from, to, opts))
export const fetchInsightsSources: InsightFetch<SourceInsights> = async (from, to, opts) => impl.fetchInsightsSources(from, to, opts)
export const fetchInsightsVisitors: InsightFetch<VisitorsReport | null> = async (from, to, opts) => impl.fetchInsightsVisitors(from, to, opts)
export const fetchInsightsSearch: InsightFetch<SearchReport | null> = async (from, to, opts) => impl.fetchInsightsSearch(from, to, opts)
export const fetchInsightsBusiness: InsightFetch<BusinessProfileReport | null> = async (from, to, opts) => impl.fetchInsightsBusiness(from, to, opts)
export const fetchInsightsSocial: InsightFetch<SocialReport | null> = async (from, to, opts) => impl.fetchInsightsSocial(from, to, opts)
export const fetchInsightsMail: InsightFetch<MailInsights | null> = async (from, to, opts) => impl.fetchInsightsMail(from, to, opts)
export const fetchIntegrations = async (opts?: RequestOptions): Promise<Integrations> => impl.fetchIntegrations(opts)
export const refreshIntegration = async (id: IntegrationId): Promise<IntegrationStatus> => impl.refreshIntegration(id)

// ——— Voorraad per plek (transacties: wachten op de server) ———

export const fetchStock = async (opts?: RequestOptions): Promise<StockOverview> => impl.fetchStock(opts)
export const fetchStockMovements = async (itemId: string, opts?: RequestOptions): Promise<StockMovement[]> => impl.fetchStockMovements(itemId, opts)
/** Eén `requestId` per poging: een herhaling boekt nooit dubbel. */
export const addStockMovement = async (req: StockMovementRequest): Promise<StockItem> => impl.addStockMovement(req)
export const addStockItem = async (req: NewStockItemRequest): Promise<StockItem> => impl.addStockItem(req)
export const patchStockItem = async (id: string, patch: StockItemPatch): Promise<StockItem> => impl.patchStockItem(id, patch)
export const exportStockMovements = async (from: string, to: string): Promise<string> => impl.exportStockMovements(from, to)
export const fetchStockReceipts = async (opts?: RequestOptions): Promise<PurchaseReceipt[]> => impl.fetchStockReceipts(opts)
/** Factuur + regels in één keer. Eén `requestId` per poging: nooit dubbel geboekt. */
export const addStockReceipt = async (req: PurchaseReceiptRequest): Promise<PurchaseReceipt> => impl.addStockReceipt(req)
export const fetchStockCounts = async (opts?: RequestOptions): Promise<StockCount[]> => impl.fetchStockCounts(opts)
export const addStockCount = async (req: StockCountRequest): Promise<StockCountResult> => impl.addStockCount(req)

// ——— Kassa (transacties: wachten op de server) ———

export const fetchKassaDay = async (day: string, opts?: RequestOptions): Promise<KassaDay> => impl.fetchKassaDay(day, opts)
/** Eén `requestId` per poging: een herhaling rekent nooit twee keer af. */
export const createSale = async (req: SaleRequest): Promise<Sale> => impl.createSale(req)
export const voidSale = async (id: string, req: VoidSaleRequest): Promise<Sale> => impl.voidSale(id, req)
export const returnSale = async (id: string, req: SaleReturnRequest): Promise<SaleReturn> => impl.returnSale(id, req)
export const closeKassaDay = async (day: string, req: CloseDayRequest): Promise<KassaDay> => impl.closeKassaDay(day, req)
export const fetchCashDayCloses = async (from: string, to: string, opts?: RequestOptions): Promise<CashDayClose[]> => impl.fetchCashDayCloses(from, to, opts)

// ——— Cadeaubonnen en tegoed (transacties: wachten op de server) ———

export const fetchVouchers = async (query: VoucherQuery, opts?: RequestOptions): Promise<VoucherPage> => impl.fetchVouchers(query, opts)
/** `null` als de bon niet bestaat. Met de volledige code: de server logt wie hem inzag. */
export const fetchVoucher = async (id: string, opts?: RequestOptions): Promise<Voucher | null> => impl.fetchVoucher(id, opts)
/** Eén `requestId` per poging: een herhaling maakt nooit een tweede bon. */
export const createVoucher = async (req: NewVoucherRequest): Promise<Voucher> => impl.createVoucher(req)
/** Correctie met reden; eén `requestId` per poging. */
export const correctVoucher = async (id: string, req: VoucherCorrectionRequest): Promise<Voucher> => impl.correctVoucher(id, req)
export const blockVoucher = async (id: string, req: VoucherBlockRequest): Promise<Voucher> => impl.blockVoucher(id, req)
/** Vooruitbetaald en openstaand (cadeaubonnen, kuren, tegoed) op het eind van `on`. */
export const fetchLiabilities = async (on: string, opts?: RequestOptions): Promise<LiabilitiesReport> =>
  normalizeLiabilities(await impl.fetchLiabilities(on, opts))
export const fetchCustomerCredit = async (id: string, opts?: RequestOptions): Promise<CustomerCredit> => impl.fetchCustomerCredit(id, opts)
export const correctCustomerCredit = async (id: string, req: CreditCorrectionRequest): Promise<CustomerCredit> =>
  impl.correctCustomerCredit(id, req)

// ——— Vindbaarheid ———

export const fetchRedirects = async (opts?: RequestOptions): Promise<RedirectsResponse> => impl.fetchRedirects(opts)
/** De hele lijst in één keer, met de `revision` waarop hij gebaseerd is. */
export const saveRedirects = async (req: RedirectsRequest): Promise<RedirectsResponse> => impl.saveRedirects(req)

// ——— Mail & nieuwsbrief (wachten op de server; campagnes zijn transacties) ———

export const fetchMailSettings = async (opts?: RequestOptions): Promise<MailSettings> => impl.fetchMailSettings(opts)
export const saveMailSettings = async (settings: MailSettings): Promise<MailSettings> => impl.saveMailSettings(settings)
export const sendMailTest = async (key: string, req: MailTestRequest): Promise<OkResponse> => impl.sendMailTest(key, req)
/** Eén `requestId` per poging: een herhaling verstuurt nooit twee keer. */
export const sendNewsletter = async (id: string, req: NewsletterSendRequest): Promise<NewsletterDeliveryResponse> => impl.sendNewsletter(id, req)
export const scheduleNewsletter = async (id: string, req: NewsletterScheduleRequest): Promise<NewsletterDeliveryResponse> =>
  impl.scheduleNewsletter(id, req)
export const sendNewsletterTest = async (id: string, req: MailTestRequest): Promise<OkResponse> => impl.sendNewsletterTest(id, req)
export const fetchSubscribers = async (query: SubscriberQuery, opts?: RequestOptions): Promise<SubscriberPage> => impl.fetchSubscribers(query, opts)
export const fetchSegments = async (opts?: RequestOptions): Promise<SegmentList> => impl.fetchSegments(opts)
export const saveSegments = async (req: SegmentsRequest): Promise<SegmentList> => impl.saveSegments(req)

/** Een aanvraag bevestigen: uit een klantdossier, of van een gast. */
export async function confirmBooking(booking: InboxBooking): Promise<InboxBooking> {
  if (!booking.customerId) return impl.confirmGuestBooking(booking.id)
  await impl.confirmAppointment(booking.customerId, booking.id)
  return { ...booking, status: 'bevestigd' }
}
